import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  DesignServices as ContentCutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import placeServiceProfessionalsService from '../../services/placeServiceProfessionalsService';
import { usePlacesData } from '../../contexts/PlacesDataContext';
import ServicesManagerDialog from '../services/ServicesManagerDialog';

/**
 * ProfessionalServicesDialog
 * 
 * Dialog para configurar quais serviços um profissional pode realizar em um local.
 * 
 * Features:
 * - Lista todos os serviços disponíveis no local
 * - Permite marcar/desmarcar quais o profissional pode realizar
 * - Salva/remove vínculos PlaceServiceProfessional
 */
export default function ProfessionalServicesDialog({ 
  open, 
  onClose, 
  placeProfessional,
  place,
  onUpdate 
}) {
  const { getService } = usePlacesData();
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [servicesManagerOpen, setServicesManagerOpen] = useState(false);

  // Carregar serviços disponíveis e os já vinculados ao profissional
  useEffect(() => {
    if (open && placeProfessional && place) {
      loadData();
    }
  }, [open, placeProfessional, place]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Os serviços disponíveis no local vêm da prop 'place'
      const placeServices = place.services || [];
      setServices(placeServices);
      console.log('[ProfessionalServicesDialog] loadData: placeServices:', placeServices.map(s => ({ id: s.id, serviceId: s.serviceId })));

      // Os serviceIds já vinculados ao profissional vêm do placeProfessional
      const existingServiceIds = placeProfessional.serviceIds || [];
      console.log('[ProfessionalServicesDialog] loadData: existingServiceIds from placeProfessional.serviceIds:', existingServiceIds);
      setSelectedServices(new Set(existingServiceIds));
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      setError('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleService = (serviceId) => {
    console.log('[ProfessionalServicesDialog] handleToggleService called with:', serviceId);
    setSelectedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
        console.log('[ProfessionalServicesDialog] Removed serviceId:', serviceId, 'newSet:', newSet);
      } else {
        newSet.add(serviceId);
        console.log('[ProfessionalServicesDialog] Added serviceId:', serviceId, 'newSet:', newSet);
      }
      return newSet;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Identificar quais serviços adicionar e quais remover
      const existingIds = new Set(placeProfessional.serviceIds || []);
      const toAdd = [...selectedServices].filter(id => !existingIds.has(id));
      const toRemove = [...existingIds].filter(id => !selectedServices.has(id));

      console.log('[ProfessionalServicesDialog] placeProfessional structure:', Object.keys(placeProfessional), placeProfessional);
      console.log('[ProfessionalServicesDialog] existingIds:', existingIds);
      console.log('[ProfessionalServicesDialog] selectedServices:', selectedServices);
      console.log('[ProfessionalServicesDialog] toAdd:', toAdd);
      console.log('[ProfessionalServicesDialog] toRemove:', toRemove);

      // Adicionar novos vínculos
      for (const serviceId of toAdd) {
        const placeService = services.find(s => s.serviceId === serviceId);
        if (placeService) {
          console.log('[ProfessionalServicesDialog] Adding service:', serviceId, 'placeServiceId:', placeService.id);
          // Backend espera: { placeProfessionalId }
          // O placeServiceId vai na URL
          await placeServiceProfessionalsService.create(
            place.id, 
            placeService.id, // Este é o PlaceServiceId (ID da relação place+service)
            { placeProfessionalId: placeProfessional.id }
          );
        }
      }

      // Remover vínculos desmarcados
      for (const serviceId of toRemove) {
        const placeService = services.find(s => s.serviceId === serviceId);
        console.log('[ProfessionalServicesDialog] Removing service:', serviceId, 'placeService:', placeService);
        
        if (placeService) {
          // Buscar a lista de profissionais vinculados para encontrar o ID do vínculo
          console.log('[ProfessionalServicesDialog] Fetching links for placeServiceId:', placeService.id);
          const existingLinks = await placeServiceProfessionalsService.getByPlaceServiceId(
            place.id,
            placeService.id
          );
          
          console.log('[ProfessionalServicesDialog] existingLinks:', existingLinks);
          if (existingLinks.length > 0) {
            console.log('[ProfessionalServicesDialog] existingLinks[0] structure:', Object.keys(existingLinks[0]), existingLinks[0]);
            console.log('[ProfessionalServicesDialog] Comparando: link.professionalId vs placeProfessional.professionalId');
            console.log('[ProfessionalServicesDialog]   link.professionalId:', existingLinks[0].professionalId);
            console.log('[ProfessionalServicesDialog]   placeProfessional.professionalId:', placeProfessional.professionalId);
          }
          
          // Encontrar o vínculo específico deste profissional
          // NOTE: getByPlaceServiceId retorna links com 'professionalId', não 'placeProfessionalId'
          const linkToDelete = existingLinks.find(
            link => link.professionalId === placeProfessional.professionalId
          );
          
          console.log('[ProfessionalServicesDialog] linkToDelete:', linkToDelete);
          
          if (linkToDelete) {
            console.log('[ProfessionalServicesDialog] Deleting link:', linkToDelete.id);
            await placeServiceProfessionalsService.delete(
              place.id,
              placeService.id,
              linkToDelete.id
            );
          } else {
            console.warn('[ProfessionalServicesDialog] ⚠️ No link found to delete for serviceId:', serviceId, 'placeProfessionalId:', placeProfessional.id);
          }
        } else {
          console.warn('[ProfessionalServicesDialog] ⚠️ placeService not found for serviceId:', serviceId);
        }
      }

      if (onUpdate) {
        await onUpdate();
      }
      
      onClose();
    } catch (err) {
      console.error('Erro ao salvar configurações:', err);
      setError('Erro ao salvar: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2} justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1 }}>
            <ContentCutIcon color="primary" />
            <Box>
              <Typography variant="h6">
                Serviços do Profissional
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {placeProfessional?.professionalName || 'Profissional'}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Gerenciar catálogo de serviços">
            <IconButton onClick={() => setServicesManagerOpen(true)} size="small">
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : services.length === 0 ? (
          <Alert severity="info">
            Este local ainda não possui serviços cadastrados. Adicione serviços ao local primeiro.
          </Alert>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Selecione quais serviços este profissional pode realizar neste local.
            </Alert>

            <List disablePadding>
              {services.map((placeService) => {
                const serviceId = placeService.serviceId;
                const service = getService(serviceId);
                const isSelected = selectedServices.has(serviceId);
                
                return (
                  <ListItem 
                    key={placeService.id} 
                    dense 
                    disableGutters
                    sx={{ 
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer'
                    }}
                    onClick={() => handleToggleService(serviceId)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={service?.name || 'Serviço'}
                      secondary={service?.category}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving || loading}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </DialogActions>

      {/* Services Manager Dialog */}
      <ServicesManagerDialog
        open={servicesManagerOpen}
        onClose={() => {
          setServicesManagerOpen(false);
          // Reload services after modal closes
          loadData();
        }}
      />
    </Dialog>
  );
}

ProfessionalServicesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  placeProfessional: PropTypes.object,
  place: PropTypes.object,
  onUpdate: PropTypes.func,
};
